/**
 * The one place a status gets its colour, so the same status looks the same on
 * every page. Keys are lower-case with underscores; statusTone() normalises
 * "To be reviewed" and "to-be-reviewed" onto "to_be_reviewed".
 */
export type BadgeTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

const TONES: Record<BadgeTone, string[]> = {
  // Done or good.
  success: [
    'accepted',
    'active',
    'answered',
    'approved',
    'certified',
    'complete',
    'completed',
    'confirmed',
    'connected',
    'correct',
    'included',
    'low',
    'low_risk',
    'on_track',
    'opportunity',
    'passed',
    'published',
    'remembered',
    'resolved',
    'success',
    'valid',
    'verified',
  ],
  // Under way.
  info: [
    'evidenced',
    'importing',
    'in_progress',
    'in_review',
    'invited',
    'processing',
    'queued',
    'recommended',
    'running',
    'sent',
    'submitted',
  ],
  // Needs attention.
  warning: [
    'at_risk',
    'excluded',
    'expiring',
    'expiring_soon',
    'material',
    'medium',
    'medium_risk',
    'missing_evidence',
    'needs_confirmation',
    'needs_review',
    'on_hold',
    'partial',
    'pending',
    'pending_approval',
    'skip',
    'to_be_reviewed',
    'warning',
  ],
  // Failed, refused or overdue.
  danger: [
    'blocked',
    'critical',
    'declined',
    'error',
    'expired',
    'failed',
    'failed_check',
    'high',
    'high_risk',
    'mandatory',
    'missed',
    'off_track',
    'overdue',
    'rejected',
    'risk',
  ],
  // Not started, or does not apply.
  neutral: [
    'archived',
    'cancelled',
    'draft',
    'impact',
    'inactive',
    'minor',
    'n/a',
    'none',
    'not_applicable',
    'not_relevant',
    'not_scored',
    'not_scored_yet',
    'not_started',
    'official',
    'self',
    'todo',
    'unknown',
  ],
};

const TONE_BY_STATUS = new Map<string, BadgeTone>(
  (Object.entries(TONES) as [BadgeTone, string[]][]).flatMap(([tone, statuses]) =>
    statuses.map((status): [string, BadgeTone] => [status, tone]),
  ),
);

const normalise = (status: string) => status.trim().toLowerCase().replace(/[\s-]+/g, '_');

/** The tone for a status. Unrecognised statuses are neutral. */
export const statusTone = (status: string | null | undefined): BadgeTone =>
  TONE_BY_STATUS.get(normalise(status ?? '')) ?? 'neutral';

/** "to_be_reviewed" → "To be reviewed". */
export const statusLabel = (status: string | null | undefined): string => {
  const words = (status ?? '').trim().replace(/[_-]+/g, ' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
};
